import { type FormEvent, useState } from "react";
import { apiPost, ApiRequestError } from "../../lib/api";
import { mapZodErrors } from "../../lib/forms";
import { usePublicConfig } from "../../lib/public-config";
import { contactSchema } from "../../shared/schemas";
import { Button } from "../ui/Button";
import { InputField, TextareaField } from "../ui/FormField";
import { StatusBanner } from "../ui/StatusBanner";
import { TurnstileField } from "./TurnstileField";

interface ContactFormState {
  name: string;
  email: string;
  subject: string;
  message: string;
  website: string;
  turnstileToken: string;
}

const initialState: ContactFormState = {
  name: "",
  email: "",
  subject: "",
  message: "",
  website: "",
  turnstileToken: "",
};

export const ContactForm = () => {
  const { config } = usePublicConfig();
  const [values, setValues] = useState<ContactFormState>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [turnstileKey, setTurnstileKey] = useState(0);

  const onChange = (field: keyof ContactFormState, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
    setFormError(null);
    setSuccess(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setFormError(null);
    setSuccess(null);

    const parsed = contactSchema.safeParse(values);

    if (!parsed.success) {
      setErrors(mapZodErrors(parsed.error));
      setSubmitting(false);
      return;
    }

    try {
      const response = await apiPost<{ ok: true; message: string }>("/api/contact", parsed.data);
      setValues(initialState);
      setErrors({});
      setSuccess(response.message);
      setTurnstileKey((current) => current + 1);
    } catch (error) {
      if (error instanceof ApiRequestError) {
        setErrors(error.fieldErrors ?? {});
        setFormError(error.message);
      } else {
        setFormError("Your message could not be sent right now. Please try again shortly.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="max-w-none space-y-6" onSubmit={handleSubmit} noValidate>
      {config && !config.contactConfigured ? (
        <StatusBanner
          tone="neutral"
          title="Contact form not configured for this deployment"
          message="Microsoft 365 delivery details have not been configured on this live Worker yet, so message submission is currently disabled."
        />
      ) : null}

      <div className="grid gap-5 md:grid-cols-2">
        <InputField
          id="contact-name"
          label="Name"
          placeholder="Jane Doe"
          value={values.name}
          onChange={(event) => onChange("name", event.target.value)}
          error={errors.name}
          autoComplete="name"
          className="py-2.5 text-base"
        />
        <InputField
          id="contact-email"
          label="Email"
          type="email"
          placeholder="jane@example.com"
          value={values.email}
          onChange={(event) => onChange("email", event.target.value)}
          error={errors.email}
          autoComplete="email"
          className="py-2.5 text-base"
        />
      </div>

      <InputField
        id="contact-subject"
        label="Subject"
        placeholder="How can we partner with you?"
        value={values.subject}
        onChange={(event) => onChange("subject", event.target.value)}
        error={errors.subject}
        className="py-2.5 text-base"
      />

      <TextareaField
        id="contact-message"
        label="Message"
        placeholder="Enter your message here..."
        value={values.message}
        onChange={(event) => onChange("message", event.target.value)}
        error={errors.message}
        hint="Please share enough context for a thoughtful follow-up."
        className="min-h-[140px] py-2.5 text-base"
        rows={5}
      />

      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
        value={values.website}
        onChange={(event) => onChange("website", event.target.value)}
      />

      <TurnstileField
        key={turnstileKey}
        siteKey={config?.turnstileSiteKey ?? null}
        onTokenChange={(token) => onChange("turnstileToken", token ?? "")}
      />

      {formError ? <StatusBanner tone="error" title="Message not sent" message={formError} /> : null}
      {success ? <StatusBanner tone="success" title="Message received" message={success} /> : null}

      <div className="flex flex-col gap-3 border-t border-white/10 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm leading-6 text-slate-400">
          Messages are reviewed by the organization and routed to internal recipients.
        </p>
        <Button type="submit" variant="lime" disabled={submitting || (config ? !config.contactConfigured : false)}>
          {submitting ? "Sending..." : "Send"}
        </Button>
      </div>
    </form>
  );
};
